// src/contexts/MetaContext.js - what the server says about itself (GET /api/meta).
//
// * environment: the test server (staging) shows a banner and hides registration;
// * features: switches for whole features (the organiser may get them step by step);
// * seasons / currentSeason: one shared idea of "this season" for every page;
//   latestHeldSeason: the last season with a competition already danced (statistics, wildcards);
// * ageRules: the category age table (requirement 2.5) - the same numbers the server checks.
// Until the answer arrives (or when it fails) safe defaults are used.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import api from '../services/api';

export const DEFAULT_AGE_RULES = {
  maxToleranceShare: 0.5,
  categories: {
    BABY: { min: 3, max: 8, toleranceYounger: 0, toleranceOlder: 1, label: 'Baby' },
    CHILDREN: { min: 6, max: 12, toleranceYounger: 2, toleranceOlder: 1, label: 'Děti' },
    JUNIOR: { min: 11, max: 16, toleranceYounger: 2, toleranceOlder: 1, label: 'Junioři' },
    ADULT: { min: 16, max: null, toleranceYounger: 2, toleranceOlder: 0, label: 'Dospělí' },
    SENIOR: { min: 25, max: null, toleranceYounger: 0, toleranceOlder: 0, label: 'Senioři' },
  },
};

// Off until /api/meta says otherwise (test-server tools and previews not released yet).
const OPT_IN_FEATURES = ['impersonation', 'diplomas'];

const DEFAULT_META = {
  environment: 'production',
  isStaging: false,
  features: {},
  currentSeason: new Date().getFullYear(),
  latestHeldSeason: null,
  seasons: [],
  ageRules: DEFAULT_AGE_RULES,
  registrationEnabled: true,
  passwordResetEnabled: true,
  leaderLoginEnabled: true,
  snapshotInfo: null,
  guideAvailable: false,
  previewFeatures: [], // only for our preview accounts: features the organiser does not see yet
  loaded: false,
};

// Without a provider (unit tests) there is nothing to wait for: loaded.
export const MetaContext = createContext({
  ...DEFAULT_META,
  loaded: true,
  featureEnabled: () => true,
  isPreview: () => false,
  reload: () => {},
});

export function MetaProvider({ children, initial }) {
  const [meta, setMeta] = useState(() => ({ ...DEFAULT_META, ...(initial || {}) }));

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get('/meta');
      if (data && typeof data === 'object') {
        setMeta({ ...DEFAULT_META, ...data, ageRules: data.ageRules || DEFAULT_AGE_RULES, loaded: true });
      }
    } catch (e) {
      setMeta((m) => ({ ...m, loaded: true }));
    }
  }, []);

  useEffect(() => {
    if (!initial) reload();
  }, [initial, reload]);

  const value = useMemo(() => {
    // A feature is on unless the server explicitly says it is off; opt-in features (view as
    // group, previews not released yet) only when the server says they are on.
    const featureEnabled = (name) => {
      if (OPT_IN_FEATURES.includes(name)) return meta.features?.[name] === true;
      return meta.features?.[name] !== false;
    };
    const isPreview = (name) => (meta.previewFeatures || []).includes(name);
    return { ...meta, featureEnabled, isPreview, reload };
  }, [meta, reload]);

  return <MetaContext.Provider value={value}>{children}</MetaContext.Provider>;
}

export const useMeta = () => useContext(MetaContext);

/** Season for pages that look back (statistics, wildcards): the last one already danced. */
export function lookBackSeason(meta) {
  return meta.latestHeldSeason || meta.currentSeason;
}

/** Seasons for a picker: the server's list plus the current season, newest first. */
export function seasonOptions(meta, extra = []) {
  const set = new Set([...(meta.seasons || []), meta.currentSeason, ...extra].filter(Boolean));
  return [...set].sort((a, b) => b - a);
}
